// scheduling/components/reports/GenTrackReport.jsx
// Gen Track balance — Tax vs Assurance for General-track staff (Trello #309).
//
// Replaces a Power BI report DJB maintains by hand. Two things it adds over that
// report: the FORWARD schedule split (his actual ask — "so we can see if we are on the
// right track to fixing any that are out of balance") and a projected column that
// combines the two.
//
// ─── THE TABLE IS THREE LABELLED PHASES ─────────────────────────────────────
// Bare percentages labelled "Actual / Forward / Projected" were unreadable, so the
// columns are three groups of IDENTICAL shape — Tax hours · Assurance hours · Tax % ·
// Assurance % — which is what makes them comparable, each visually distinct:
//
//   CHARGED (WIP)     what was actually charged in PE (the Power BI equivalent)
//   SCHEDULED AHEAD   what is planned, tinted to read as not-yet-real
//   PROJECTED         charged WIP + future scheduled — the only group Band judges
//
// Both percentages are shown even though Assurance % is just 100 − Tax %: DJB's Power BI
// shows both, and reading "37% tax" and inferring "so 63% assurance" is arithmetic the
// table should do for you when the whole point is judging a two-sided balance.
//
// "Other" hours (ADMIN, PTO, CAS…) are no longer a column. They are excluded from every
// ratio by design, and showing ~31% of someone's time in a column that feeds nothing
// invited the reading that the percentages didn't add up.
//
// All ratio/band/trend logic lives in utils/genTrack.js, shared with the Staff
// Suggestions badge so the two surfaces cannot disagree.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../../../hooks/useUrlState';
import { fetchGenTrackBalance } from '../../services/reportsApi';
import { useSort, SortableTh, PlainTh } from './SortableTh';
import {
  decorateRow, tallyBands, tallyTrends, BANDS, BAND_META, TREND_META, TRENDS,
  BAND_LOW_PCT, BAND_HIGH_PCT, MIN_ACTUAL_HOURS, MIN_FORWARD_HOURS, fmtHours,
} from '../../utils/genTrack';

const BASIS_OPTIONS = [
  // All-time is first AND default: it is the only basis that isn't distorted by the
  // Jan–Apr tax / May–Dec audit seasonality. See the header comment in
  // api/rprsReports/gentrack.js for the measurements behind that.
  { id: 'all', label: 'All time' },
  { id: 'r12', label: 'Rolling 12mo' },
  { id: 'cy', label: 'Calendar yr' },
  { id: 'fy', label: 'Fiscal yr' },
];

// key -> value extractor + the direction to use when the column is first picked.
const SORTS = {
  name: { get: r => String(r.name).toLowerCase(), dir: 'asc' },
  level: { get: r => String(r.level || ''), dir: 'asc' },
  office: { get: r => String(r.office || ''), dir: 'asc' },
  hireDate: { get: r => r.hireDate || '', dir: 'desc' },
  firstWipDate: { get: r => r.firstWipDate || '', dir: 'asc' },
  taxHours: { get: r => r.taxHours, dir: 'desc' },
  assurHours: { get: r => r.assurHours, dir: 'desc' },
  actualPct: { get: r => r.actualPct, dir: 'asc' },
  fwdTax: { get: r => r.fwdTax, dir: 'desc' },
  fwdAssur: { get: r => r.fwdAssur, dir: 'desc' },
  forwardPct: { get: r => r.forwardPct, dir: 'asc' },
  projectedPct: { get: r => r.projectedPct, dir: 'asc' },
  deltaPct: { get: r => r.deltaPct, dir: 'asc' },
};

// Tax | Assurance split as one bar, with the in-balance zone marked. The number alone
// makes you do arithmetic; the bar makes "leaning too far one way" visible at a glance,
// which is what the Power BI version is actually used for.
function SplitBar({ pct, planned }) {
  if (pct == null) {
    return <div className="sched-rpt-bar"><div className="sched-rpt-bar-empty">—</div></div>;
  }
  return (
    <div className={`sched-rpt-bar${planned ? ' sched-rpt-bar--planned' : ''}`}>
      <div className="sched-rpt-bar-tax" style={{ width: `${pct}%` }} />
      <div className="sched-rpt-bar-asr" style={{ width: `${100 - pct}%` }} />
      {/* 40% and 60% guide rails — the band DJB called "in balance". */}
      <div className="sched-rpt-bar-tick" style={{ left: `${BAND_LOW_PCT}%` }} />
      <div className="sched-rpt-bar-tick" style={{ left: `${BAND_HIGH_PCT}%` }} />
    </div>
  );
}

// Charged above, projected below, same scale. Two stacked bars in one column cost no
// extra width and show the DIRECTION of the move, which a single bar cannot. The
// micro-labels mean it reads without hovering.
function DualBar({ chargedPct, projectedPct }) {
  return (
    <div className="sched-rpt-dual">
      <div className="sched-rpt-dual-row">
        <span className="sched-rpt-dual-lbl">now</span>
        <SplitBar pct={chargedPct} />
      </div>
      <div className="sched-rpt-dual-row">
        <span className="sched-rpt-dual-lbl">proj</span>
        <SplitBar pct={projectedPct} planned />
      </div>
    </div>
  );
}

function BandChip({ band }) {
  const m = BAND_META[band] || BAND_META[BANDS.UNKNOWN];
  return <span className="sched-rpt-chip" style={{ background: m.bg, color: m.fg }} title={m.label}>{m.short}</span>;
}

// Signed points-of-tax-share move, coloured AND worded by whether it heads toward the
// band. Not colour alone: "further" reads the same in greyscale.
function TrendCell({ trend, deltaPct }) {
  const m = TREND_META[trend] || TREND_META[TRENDS.UNKNOWN];
  if (trend === TRENDS.UNKNOWN || deltaPct == null) {
    return <span className="sched-rpt-muted-sm" title={m.title}>—</span>;
  }
  const sign = deltaPct > 0 ? '+' : '';
  return (
    <span className="sched-rpt-trend" style={{ background: m.bg, color: m.fg }} title={m.title}>
      {sign}{deltaPct} {m.word}
    </span>
  );
}

// One half of a two-sided ratio. `side` only changes the tooltip wording — the value is
// always the tax share or its complement, so the two can never disagree.
function PctCell({ pct, hours, minHours, side = 'tax', strong }) {
  const shown = pct == null ? null : (side === 'tax' ? pct : 100 - pct);
  const thin = shown != null && (hours || 0) < minHours;
  const cls = ['sched-rpt-pct', thin ? 'sched-rpt-pct--thin' : '', strong ? 'sched-rpt-pct--strong' : '']
    .filter(Boolean).join(' ');
  return (
    <span
      className={cls}
      title={thin
        ? `Only ${fmtHours(hours)}h of tax+assurance work — too few to read a ratio from`
        : `${fmtHours(hours)}h of tax+assurance work in total`}
    >
      {shown == null ? '—' : `${shown}%`}
    </span>
  );
}

export default function GenTrackReport() {
  const navigate = useNavigate();
  const [basis, setBasis] = useUrlState('basis', 'all', { validate: BASIS_OPTIONS.map(b => b.id) });
  const [showIncoming, setShowIncoming] = useState(false);
  // Intern time is OUT by default and the flags never include it; this toggle exists
  // only so the report can show the lifetime picture on request. In the URL so a view
  // can be shared without the recipient silently seeing different numbers.
  const [includeIntern, setIncludeIntern] = useUrlState('intern', false);
  const sort = useSort(SORTS, 'projectedPct', 'gtsort');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchGenTrackBalance({ basis, includeIntern })
      .then(r => { if (!cancelled) { setData(r); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [basis, includeIntern]);

  const { started, incoming, tally, trendTally } = useMemo(() => {
    const rows = (data?.rows || []).map(decorateRow);
    const s = sort.sortRows(rows.filter(r => !r.isIncoming));
    const i = [...rows.filter(r => r.isIncoming)].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return { started: s, incoming: i, tally: tallyBands(s), trendTally: tallyTrends(s) };
  }, [data, sort]);

  // What the toggle is actually withholding, so "excluded" is a number rather than a
  // vibe. Reported by the server in both modes.
  const internHours = useMemo(
    () => (data?.rows || []).filter(r => !r.isIncoming)
      .reduce((t, r) => t + (r.internTax || 0) + (r.internAssur || 0), 0),
    [data],
  );

  const taxList = data?.taxCodes?.join(', ') || '';
  const asrList = data?.assuranceCodes?.join(', ') || '';

  return (
    <div className="sched-rpt-root">
      {/* ── Basis toggle ── */}
      <div className="sched-cap-toolbar">
        <span className="sched-cap-toolbar-label" title="Which window of charged WIP the CHARGED columns cover. Does not affect Scheduled Ahead, which is always today onward.">BASIS</span>
        {BASIS_OPTIONS.map(b => (
          <button key={b.id}
            className={`sched-mgr-pill${basis === b.id ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setBasis(b.id)}
          >{b.label}</button>
        ))}
        {data && (
          <span className="sched-rpt-window">
            {data.basisLabel}
            {basis !== 'all' && <> &middot; {data.from} &ndash; {data.to}</>}
          </span>
        )}
      </div>

      {/* ── Intern time ── */}
      <div className="sched-cap-toolbar">
        <span className="sched-cap-toolbar-label"
          title="Hours charged before a staffer went full time -- their internship. Excluded by default.">INTERN TIME</span>
        <button
          className={`sched-mgr-pill${!includeIntern ? ' sched-mgr-pill--active' : ''}`}
          onClick={() => setIncludeIntern(false)}
          title="Reset everyone to zero at their full-time start date. Internships are predominantly tax, so counting them forever means a staffer never reads as needing tax work. This is what the Staff Suggestions flags always use."
        >Exclude</button>
        <button
          className={`sched-mgr-pill${includeIntern ? ' sched-mgr-pill--active' : ''}`}
          onClick={() => setIncludeIntern(true)}
          title="Count intern hours too -- the full lifetime picture. Report-only: the Staff Suggestions flags ignore this setting."
        >Include</button>
        {data && internHours > 0 && (
          <span className="sched-rpt-window">
            {includeIntern
              ? <>including {fmtHours(internHours)}h of intern time</>
              : <>excluding {fmtHours(internHours)}h of intern time</>}
          </span>
        )}
      </div>

      {/* Seasonality is the single easiest way to misread this report, so it is stated
          on screen rather than left in a code comment. */}
      {basis === 'fy' && (
        <div className="sched-rpt-warn">
          ⚠ Fiscal year starts 1 May, so early in the FY this window holds audit season and
          little or no tax season — staff will read as needing Tax regardless of their real
          balance. All time or Rolling 12mo is the honest comparison.
        </div>
      )}

      {error && <div className="sched-rpt-error">Couldn’t load: {error}</div>}
      {loading && <div className="sched-rpt-muted">Loading…</div>}

      {!loading && !error && data && (
        <>
          {/* ── Summary: two tallies, not one ──
              Bands alone say how many are out of balance; the direction tally says
              whether the schedule is fixing it. On prod today the second is the
              alarming one. */}
          <div className="sched-rpt-summary">
            <div className="sched-rpt-statgroup">
              <div className="sched-rpt-statgroup-l" title="Verdict on the PROJECTED split — charged WIP plus what is already scheduled ahead. Not the charged split alone.">
                Projected band
              </div>
              <div className="sched-rpt-statrow">
                {[BANDS.NEEDS_TAX, BANDS.BALANCED, BANDS.NEEDS_ASSURANCE, BANDS.UNKNOWN].map(b => (
                  <div key={b} className="sched-rpt-stat" style={{ background: BAND_META[b].bg }}
                    title={`${BAND_META[b].label} — based on the PROJECTED split (charged WIP + scheduled ahead)`}>
                    <div className="sched-rpt-stat-n" style={{ color: BAND_META[b].fg }}>{tally[b] || 0}</div>
                    <div className="sched-rpt-stat-l">{BAND_META[b].label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sched-rpt-statgroup">
              <div className="sched-rpt-statgroup-l" title="Whether what is scheduled ahead moves each person toward or away from the 40–60% band. This is the 'are we on the right track' answer.">
                Direction of travel
              </div>
              <div className="sched-rpt-statrow">
                {[TRENDS.FURTHER, TRENDS.CLOSER, TRENDS.STEADY].map(k => (
                  <div key={k} className="sched-rpt-stat" style={{ background: TREND_META[k].bg }}
                    title={TREND_META[k].title}>
                    <div className="sched-rpt-stat-n" style={{ color: TREND_META[k].fg }}>{trendTally[k] || 0}</div>
                    <div className="sched-rpt-stat-l">{TREND_META[k].word}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sched-rpt-legend">
              <div><span className="sched-rpt-sw sched-rpt-sw--tax" /> Tax &middot; {taxList}</div>
              <div><span className="sched-rpt-sw sched-rpt-sw--asr" /> Assurance &middot; {asrList}</div>
              <div className="sched-rpt-muted-sm">
                In balance = {BAND_LOW_PCT}&ndash;{BAND_HIGH_PCT}% Tax. Admin, PTO and CAS hours are
                excluded from every ratio.
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div className="sched-mgr-table-wrap">
            <table className="sched-mgr-table sched-rpt-table">
              <thead>
                {/* Group row. Three phases of the same question, so the per-column
                    headers below can stay short without becoming ambiguous. */}
                <tr className="sched-rpt-grouprow">
                  <th colSpan={5} className="sched-rpt-grp sched-rpt-grp--id" />
                  <th colSpan={4} className="sched-rpt-grp sched-rpt-grp--past sched-rpt-sep"
                    title={`WIP actually charged in PE over the selected basis (${data.basisLabel}). This is the historical fact — the equivalent of the Power BI report.`}>
                    Charged WIP &middot; {data.basisLabel}
                  </th>
                  <th colSpan={4} className="sched-rpt-grp sched-rpt-grp--future sched-rpt-sep"
                    title={`Hours already on the RPRS calendar from ${data.today} onward. Planned, not yet worked — and strongly seasonal, since most Gen Track work scheduled in the autumn is assurance.`}>
                    Scheduled ahead &middot; from {data.today}
                  </th>
                  <th colSpan={5} className="sched-rpt-grp sched-rpt-grp--proj sched-rpt-sep"
                    title="Historical charged WIP + future scheduled hours, combined. Where they land if nothing changes — this is the group the Band verdict reads.">
                    Projected &middot; historical WIP + future scheduled
                  </th>
                </tr>
                <tr className="sched-rpt-colrow">
                  {/* identity */}
                  <SortableTh sort={sort} sortKey="name" label="Staff"
                    title="General-track staff member (rprs.Staff.DepartmentCode = GENERAL, active, already started). Click a row to open their profile." />
                  <SortableTh sort={sort} sortKey="level" label="Level"
                    title="PE staff level. Gen Track is mostly Staff B and interns — people rotate out into a department after a year or two." />
                  <SortableTh sort={sort} sortKey="office" label="Office"
                    title="PE office code. General-track staff are spread across all six offices, and an on-site job restricts suggestions to its own office." />
                  <SortableTh sort={sort} sortKey="hireDate" label="Hired"
                    title="Current hire date from PE. NOT necessarily when they first charged time: returning interns were often here for prior busy seasons before being hired." />
                  <SortableTh sort={sort} sortKey="firstWipDate" label="1st charged"
                    title="Date of their first ever charged time entry. Where this is much earlier than the hire date they worked prior busy seasons as an intern; where it is much later, the hire date overstates their real experience." />

                  {/* charged WIP */}
                  <SortableTh sort={sort} sortKey="taxHours" label="Tax h" className="sched-mgr-th-right sched-rpt-sep"
                    title={`WIP charged to tax service lines (${taxList}) in the selected basis window.`} />
                  <SortableTh sort={sort} sortKey="assurHours" label="Assur h" className="sched-mgr-th-right"
                    title={`WIP charged to assurance service lines (${asrList}) in the selected basis window.`} />
                  <SortableTh sort={sort} sortKey="actualPct" label="Tax %" className="sched-mgr-th-right"
                    title="Tax share of charged CLIENT work: Tax / (Tax + Assurance). Admin, PTO and CAS hours are not in the denominator." />
                  <PlainTh label="Assur %" className="sched-mgr-th-right"
                    title="Assurance share of charged client work — the complement of Tax %. Shown so a two-sided balance doesn't require mental arithmetic." />

                  {/* scheduled ahead */}
                  <SortableTh sort={sort} sortKey="fwdTax" label="Tax h" className="sched-mgr-th-right sched-rpt-sep"
                    title={`Hours already scheduled on tax jobs from today forward — service lines ${taxList}, plus custom jobs categorised Tax (Tax Work, Preparer Pool, E&T Pool).`} />
                  <SortableTh sort={sort} sortKey="fwdAssur" label="Assur h" className="sched-mgr-th-right"
                    title={`Hours already scheduled on assurance jobs from today forward — service lines ${asrList}, plus custom jobs categorised Assurance (EBP Team).`} />
                  <SortableTh sort={sort} sortKey="forwardPct" label="Tax %" className="sched-mgr-th-right"
                    title="Tax share of what is scheduled ahead. Read with care: it is structurally seasonal, so a low number in the autumn is usually the calendar rather than the person." />
                  <PlainTh label="Assur %" className="sched-mgr-th-right"
                    title="Assurance share of what is scheduled ahead — the complement of Tax %." />

                  {/* projected */}
                  <SortableTh sort={sort} sortKey="projectedPct" label="Tax %" className="sched-mgr-th-right sched-rpt-sep"
                    title="Projected tax share: (charged tax WIP + scheduled tax) / (charged + scheduled client work). This is the number the Band column judges." />
                  <PlainTh label="Assur %" className="sched-mgr-th-right"
                    title="Projected assurance share — the complement of the projected Tax %." />
                  <SortableTh sort={sort} sortKey="deltaPct" label="Move"
                    title="How far the projected split shifts from the charged split, in points of tax share — and whether that heads TOWARD the 40–60% band (“closer”) or away from it (“further”). This is the “are we on the right track” column." />
                  <PlainTh label="Split"
                    title="Top bar = charged split, bottom (outlined) = projected. Same scale, so the gap between them is the direction of travel. Green = tax, teal = assurance; white ticks mark 40% and 60%." />
                  <PlainTh label="Band"
                    title={`Verdict on the PROJECTED split. In balance = ${BAND_LOW_PCT}–${BAND_HIGH_PCT}% tax. Greyed when there are under ${MIN_ACTUAL_HOURS}h of client work to judge from.`} />
                </tr>
              </thead>
              <tbody>
                {started.map(r => (
                  <tr key={r.staffId} onClick={() => navigate(`/schedule/employee/${r.staffId}`)}>
                    <td>{r.name}</td>
                    <td className="sched-rpt-muted-sm">{r.level}</td>
                    <td className="sched-rpt-muted-sm">{r.office || '—'}</td>
                    <td className="sched-rpt-muted-sm">{r.hireDate || '—'}</td>
                    <td className="sched-rpt-muted-sm">{r.firstWipDate || '—'}</td>

                    <td className="sched-mgr-td-right sched-rpt-sep">{fmtHours(r.taxHours)}</td>
                    <td className="sched-mgr-td-right">{fmtHours(r.assurHours)}</td>
                    <td className="sched-mgr-td-right"><PctCell pct={r.actualPct} hours={r.actualTotal} minHours={MIN_ACTUAL_HOURS} side="tax" /></td>
                    <td className="sched-mgr-td-right"><PctCell pct={r.actualPct} hours={r.actualTotal} minHours={MIN_ACTUAL_HOURS} side="assur" /></td>

                    <td className="sched-mgr-td-right sched-rpt-sep sched-rpt-fut">{fmtHours(r.fwdTax)}</td>
                    <td className="sched-mgr-td-right sched-rpt-fut">{fmtHours(r.fwdAssur)}</td>
                    <td className="sched-mgr-td-right sched-rpt-fut"><PctCell pct={r.forwardPct} hours={r.fwdTotal} minHours={MIN_FORWARD_HOURS} side="tax" /></td>
                    <td className="sched-mgr-td-right sched-rpt-fut"><PctCell pct={r.forwardPct} hours={r.fwdTotal} minHours={MIN_FORWARD_HOURS} side="assur" /></td>

                    <td className="sched-mgr-td-right sched-rpt-sep"><PctCell pct={r.projectedPct} hours={r.projTotal} minHours={MIN_ACTUAL_HOURS} side="tax" strong /></td>
                    <td className="sched-mgr-td-right"><PctCell pct={r.projectedPct} hours={r.projTotal} minHours={MIN_ACTUAL_HOURS} side="assur" /></td>
                    <td><TrendCell trend={r.trend} deltaPct={r.deltaPct} /></td>
                    <td><DualBar chargedPct={r.actualPct} projectedPct={r.projectedPct} /></td>
                    <td><BandChip band={r.band} /></td>
                  </tr>
                ))}
                {started.length === 0 && (
                  <tr><td colSpan={18} className="sched-rpt-muted">No General-track staff have started yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Incoming ──
              Kept out of the main table because their StartDate hasn't arrived, so they
              can't be scheduled against — but their prior intern-season history is
              exactly what you want when deciding their first assignment. Folding them
              in would put ~a third of the rows in bands nobody can act on. */}
          {incoming.length > 0 && !includeIntern && (
            <div className="sched-rpt-incoming">
              <div className="sched-rpt-muted-sm">
                {incoming.length} incoming staff hidden — everything they have charged predates
                their start date, so with intern time excluded every row would read zero. Switch
                <strong> Intern time</strong> to <strong>Include</strong> to see their prior
                seasons, which is what you want when deciding a first assignment.
              </div>
            </div>
          )}
          {incoming.length > 0 && includeIntern && (
            <div className="sched-rpt-incoming">
              <button className="sched-rpt-disclose" onClick={() => setShowIncoming(v => !v)}>
                {showIncoming ? '▾' : '▸'} Incoming — {incoming.length} not started yet
                <span className="sched-rpt-muted-sm"> (prior intern-season hours shown)</span>
              </button>
              {showIncoming && (
                <table className="sched-mgr-table sched-rpt-table">
                  <thead>
                    <tr className="sched-rpt-colrow">
                      <PlainTh label="Staff" title="Hired into General but their start date has not arrived yet, so they cannot be scheduled against." />
                      <PlainTh label="Level" title="PE staff level." />
                      <PlainTh label="Office" title="PE office code." />
                      <PlainTh label="Starts" title="Future hire date from PE. All current incoming staff share 2026-09-21." />
                      <PlainTh label="1st charged" title="First ever charged time — for these people, a prior busy season worked as an intern." />
                      <PlainTh label="Tax h" className="sched-mgr-th-right" title="Tax WIP charged in prior seasons, all time." />
                      <PlainTh label="Assur h" className="sched-mgr-th-right" title="Assurance WIP charged in prior seasons, all time." />
                      <PlainTh label="Tax %" className="sched-mgr-th-right" title="Prior-season tax share. Often extreme, because most worked exactly one season — a spring intern looks 100% tax." />
                      <PlainTh label="Assur %" className="sched-mgr-th-right" title="Prior-season assurance share — the complement of Tax %." />
                      <PlainTh label="Split" title="Prior-season split. Green = tax, teal = assurance; ticks mark the 40–60% band." />
                    </tr>
                  </thead>
                  <tbody>
                    {incoming.map(r => (
                      <tr key={r.staffId} onClick={() => navigate(`/schedule/employee/${r.staffId}`)}>
                        <td>{r.name}</td>
                        <td className="sched-rpt-muted-sm">{r.level}</td>
                        <td className="sched-rpt-muted-sm">{r.office || '—'}</td>
                        <td className="sched-rpt-muted-sm">{r.hireDate || '—'}</td>
                        <td className="sched-rpt-muted-sm">{r.firstWipDate || '—'}</td>
                        <td className="sched-mgr-td-right">{fmtHours(r.taxHours)}</td>
                        <td className="sched-mgr-td-right">{fmtHours(r.assurHours)}</td>
                        <td className="sched-mgr-td-right"><PctCell pct={r.actualPct} hours={r.actualTotal} minHours={MIN_ACTUAL_HOURS} side="tax" /></td>
                        <td className="sched-mgr-td-right"><PctCell pct={r.actualPct} hours={r.actualTotal} minHours={MIN_ACTUAL_HOURS} side="assur" /></td>
                        <td><div className="sched-rpt-dual-row"><SplitBar pct={r.actualPct} /></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="sched-rpt-footnote">
            <strong>Charged WIP</strong> is time actually entered in PE over the selected basis.
            <strong> Scheduled ahead</strong> is what is on the RPRS calendar from {data.today} on —
            planned, not worked, and structurally season-dependent since almost all Gen Track
            work scheduled in the autumn is assurance. <strong>Projected</strong> combines the two
            (historical WIP + future scheduled) and is what <strong>Band</strong> judges. Ratios from
            under {MIN_ACTUAL_HOURS}h charged (or {MIN_FORWARD_HOURS}h scheduled) are greyed rather
            than banded, since a percentage off a handful of hours is an artefact. Admin, PTO and
            CAS hours are real but belong to neither side, so they are excluded from every ratio.
          </div>
        </>
      )}
    </div>
  );
}
